import { ArrowLeft, ArrowRight, Badge, Button, Conductor, Stars02, XClose } from '@opendate-app/setlist';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { DEFAULT_VENUE_CONDUCTOR_PROMPTS } from '../defaultPrompts';
import { conductorTheme } from '../themeTokens';

const sparkleTakeoverConfig = [
  { left: '3%', size: 24, duration: 4.8, delay: 0.0, drift: -28 },
  { left: '7%', size: 18, duration: 4.1, delay: 0.4, drift: 18 },
  { left: '11%', size: 22, duration: 5.0, delay: 0.9, drift: -12 },
  { left: '15%', size: 16, duration: 3.9, delay: 1.4, drift: 22 },
  { left: '19%', size: 20, duration: 4.6, delay: 1.1, drift: -18 },
  { left: '23%', size: 28, duration: 5.3, delay: 0.2, drift: 16 },
  { left: '27%', size: 15, duration: 3.8, delay: 1.8, drift: -24 },
  { left: '31%', size: 21, duration: 4.4, delay: 0.7, drift: 14 },
  { left: '35%', size: 17, duration: 4.0, delay: 1.3, drift: -10 },
  { left: '39%', size: 26, duration: 5.1, delay: 0.5, drift: 25 },
  { left: '43%', size: 20, duration: 4.2, delay: 1.6, drift: -20 },
  { left: '47%', size: 14, duration: 3.7, delay: 0.9, drift: 12 },
  { left: '51%', size: 24, duration: 4.9, delay: 0.1, drift: -16 },
  { left: '55%', size: 18, duration: 4.1, delay: 1.4, drift: 20 },
  { left: '59%', size: 22, duration: 4.7, delay: 0.8, drift: -22 },
  { left: '63%', size: 16, duration: 3.9, delay: 1.7, drift: 10 },
  { left: '67%', size: 28, duration: 5.4, delay: 0.3, drift: -26 },
  { left: '71%', size: 19, duration: 4.3, delay: 1.0, drift: 18 },
  { left: '75%', size: 21, duration: 4.6, delay: 1.5, drift: -14 },
  { left: '79%', size: 15, duration: 3.8, delay: 0.6, drift: 26 },
  { left: '83%', size: 23, duration: 4.8, delay: 1.2, drift: -18 },
  { left: '87%', size: 18, duration: 4.2, delay: 0.0, drift: 16 },
  { left: '91%', size: 26, duration: 5.2, delay: 0.9, drift: -24 },
  { left: '95%', size: 16, duration: 3.9, delay: 1.8, drift: 12 }
];

const backdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  backgroundColor: 'rgba(15, 23, 42, 0.56)',
  backdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px'
};

const takeoverStageStyle = {
  position: 'absolute',
  inset: 0,
  overflow: 'hidden',
  pointerEvents: 'none'
};

const shellStyle = {
  position: 'relative',
  width: 'min(900px, 92vw)',
  borderRadius: '16px',
  background: conductorTheme.surface,
  border: `1px solid ${conductorTheme.border}`,
  boxShadow: '0 14px 34px rgba(10, 13, 18, 0.05), 0 30px 90px rgba(15, 23, 42, 0.24)',
  overflow: 'hidden',
  maxHeight: '92vh',
  display: 'flex',
  flexDirection: 'column',
  fontFamily: "'Open Sans', sans-serif"
};

const takeoverFloorStyle = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: '-6%',
  height: '36%',
  background: 'radial-gradient(ellipse at center bottom, rgba(255, 89, 94, 0.24) 0%, rgba(149, 123, 161, 0.18) 34%, rgba(42, 156, 228, 0.08) 58%, rgba(15, 23, 42, 0) 76%)',
  filter: 'blur(12px)'
};

const takeoverColumnStyle = (left, width, height, opacity) => ({
  position: 'absolute',
  left,
  bottom: '-10%',
  width,
  height,
  borderRadius: '999px 999px 0 0',
  background: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(42,156,228,0.08) 18%, rgba(149,123,161,0.2) 54%, rgba(255,89,94,0.34) 100%)',
  filter: 'blur(28px)',
  opacity
});

const eyebrowStyle = {
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--setlist-text-brand-primary, #1982c4)'
};

const titleStyle = {
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '32px',
  letterSpacing: '-0.63px',
  color: conductorTheme.textStrong
};

const capabilityStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '4px',
  padding: '12px 14px',
  borderRadius: '8px',
  backgroundColor: 'var(--od-c-f3f4f6, #f5f5f5)'
};

const capabilities = [
  {
    title: 'Venue-wide answers',
    description: 'Ask about upcoming events, offers, holds, and venue details.'
  },
  {
    title: 'Sales, finances, and Toast data',
    description: 'Pull ticket sales, payouts, revenue trends, and Toast F&B metrics like PHAB without leaving the chat.'
  },
  {
    title: 'Audience insights',
    description: 'Check fan markets, attendance patterns, and engagement signals.'
  }
];

const redesignHighlights = [
  {
    title: 'Right where you work',
    description: 'Drag the Ask Conductor pill anywhere, open it in place, or dock it beside the page when you need more room.',
    highlight: true
  },
  {
    title: 'See it work',
    description: 'Every answer shows the tools it ran and the sources it used, with follow-up questions to ask next.',
    highlight: true
  }
];

const getIsMobileLayout = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.innerWidth < 768;
};

const VenueConductorLaunchTour = ({
  visible,
  teamName,
  onClose,
  onStart,
  onPromptSelect,
  conductorVoiceEnabled,
  hasAiIntegration
}) => {
  const [isMobileLayout, setIsMobileLayout] = useState(getIsMobileLayout);
  const [step, setStep] = useState(1);
  const stepRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobileLayout(getIsMobileLayout());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Always open on the video step when the modal (re)appears.
  useEffect(() => {
    if (visible) {
      setStep(1);
    }
  }, [visible]);

  // Move focus to the active step so keyboard/SR users aren't dropped to <body>
  // when the panel content swaps between steps (the dots also announce progress).
  useEffect(() => {
    if (visible) {
      stepRef.current?.focus({ preventScroll: true });
    }
  }, [visible, step]);

  if (!visible) {
    return null;
  }

  const overlayStyle = {
    ...backdropStyle,
    alignItems: isMobileLayout ? 'stretch' : backdropStyle.alignItems,
    justifyContent: isMobileLayout ? 'stretch' : backdropStyle.justifyContent,
    padding: isMobileLayout ? '0' : backdropStyle.padding
  };

  const responsiveShellStyle = {
    ...shellStyle,
    width: isMobileLayout ? '100vw' : shellStyle.width,
    maxWidth: isMobileLayout ? 'none' : shellStyle.width,
    minHeight: isMobileLayout ? '100dvh' : undefined,
    height: isMobileLayout ? '100dvh' : undefined,
    maxHeight: isMobileLayout ? '100dvh' : shellStyle.maxHeight,
    borderRadius: isMobileLayout ? '0' : shellStyle.borderRadius,
    border: isMobileLayout ? 'none' : shellStyle.border
  };

  const contentPadding = isMobileLayout
    ? 'calc(env(safe-area-inset-top, 0px) + 18px) 18px calc(env(safe-area-inset-bottom, 0px) + 18px)'
    : '28px 32px 26px';

  const sparklesToRender = isMobileLayout
    ? sparkleTakeoverConfig.filter((_, index) => index % 3 === 0)
    : sparkleTakeoverConfig;
  const promptsToShow = isMobileLayout
    ? DEFAULT_VENUE_CONDUCTOR_PROMPTS.slice(0, 4)
    : DEFAULT_VENUE_CONDUCTOR_PROMPTS.slice(0, 6);

  const videoSrc = isMobileLayout ? '/conductor-redesign-4x5.mp4' : '/conductor-redesign.mp4';

  const relaunchHighlights = [...redesignHighlights];
  if (conductorVoiceEnabled) {
    relaunchHighlights.push({
      title: 'Talk to it — new voice mode',
      description: 'Tap the mic on the pill and just say what you need. Conductor listens and answers out loud.',
      highlight: true
    });
  }
  if (hasAiIntegration) {
    relaunchHighlights.push({
      title: 'Connect your own AI tools',
      description: 'Use Opendate straight from Claude, ChatGPT, Codex, and other assistants via the MCP connector — no copy-paste.',
      highlight: true
    });
  }
  const displayCapabilities = [...relaunchHighlights, ...capabilities];

  const stepDots = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }} aria-hidden="true">
      {[1, 2].map((dotStep) => (
        <span
          key={dotStep}
          style={{
            width: dotStep === step ? '22px' : '8px',
            height: '8px',
            borderRadius: '999px',
            background: dotStep === step
              ? 'linear-gradient(90deg, #2a9ce4 0%, #957ba1 50%, #ff595e 100%)'
              : conductorTheme.border,
            transition: 'width 0.25s ease, background 0.25s ease'
          }}
        />
      ))}
    </div>
  );

  const primaryButtonStyle = isMobileLayout ? { width: '100%' } : undefined;

  const closeButton = (
    <div style={{ position: 'absolute', top: '14px', right: '14px', zIndex: 3 }}>
      <Button
        variant="secondary"
        size="sm"
        icon={XClose}
        className="venue-conductor-ui-icon-button"
        aria-label="Close"
        onClick={onClose}
      />
    </div>
  );

  const videoFrame = (
    <div style={{
      position: 'relative',
      width: '100%',
      ...(isMobileLayout
        ? { padding: '12px 16px 2px', flexShrink: 0 }
        : { aspectRatio: '16 / 9', background: conductorTheme.surface, overflow: 'hidden', borderBottom: `1px solid ${conductorTheme.border}` })
    }}>
      <video
        key={videoSrc}
        src={videoSrc}
        autoPlay
        muted
        playsInline
        controls
        aria-label="See what's new in Conductor"
        style={isMobileLayout
          ? { display: 'block', width: '100%', aspectRatio: '4 / 5', maxHeight: '60vh', objectFit: 'contain', borderRadius: '12px', border: `1px solid ${conductorTheme.border}`, background: conductorTheme.surface, boxShadow: '0 14px 40px rgba(15, 23, 42, 0.18)' }
          : { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    </div>
  );

  const stepOne = (
    <div key="step-1" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto', justifyContent: isMobileLayout ? 'center' : 'flex-start', animation: 'conductor-step-in 0.35s ease' }}>
      {videoFrame}
      <div style={{
        padding: isMobileLayout ? '0 18px calc(env(safe-area-inset-bottom, 0px) + 16px)' : contentPadding,
        display: 'flex',
        flexDirection: 'column',
        flex: isMobileLayout ? '0 0 auto' : 1
      }}>
        {!isMobileLayout && (
          <>
            <div style={eyebrowStyle}>New in Opendate</div>
            <div style={{ ...titleStyle, marginTop: '6px' }}>The new Conductor</div>
            <p style={{ fontSize: '14px', lineHeight: '20px', color: conductorTheme.textSoft, margin: '6px 0 0 0' }}>
              A pill you can drag anywhere, a panel that shows its work on every answer, and a dock when you need
              more room — with instant answers across {teamName || 'your venues'}.
            </p>
          </>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          marginTop: isMobileLayout ? '16px' : '24px',
          flexDirection: isMobileLayout ? 'column-reverse' : 'row'
        }}>
          {isMobileLayout ? <div style={{ alignSelf: 'center' }}>{stepDots}</div> : stepDots}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: isMobileLayout ? '100%' : undefined,
            flexDirection: isMobileLayout ? 'column-reverse' : 'row'
          }}>
            <Button variant="tertiary" size="md" style={primaryButtonStyle} onClick={onClose}>Skip</Button>
            <Button variant="primary" size="md" style={primaryButtonStyle} onClick={() => setStep(2)}>
              See what&apos;s new <ArrowRight width={16} height={16} aria-hidden="true" style={{ marginLeft: '6px' }} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const stepTwo = (
    <div key="step-2" style={{ display: 'flex', flexDirection: 'column', flex: 1, padding: contentPadding, animation: 'conductor-step-in 0.35s ease', minHeight: 0, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: isMobileLayout ? '14px' : '18px' }}>
        <Conductor width={isMobileLayout ? 32 : 40} height={isMobileLayout ? 32 : 40} animation="constellation-twinkle" />
        <div>
          <div style={eyebrowStyle}>Here&apos;s what&apos;s new</div>
          <div style={{ ...titleStyle, fontSize: isMobileLayout ? '20px' : titleStyle.fontSize, marginTop: '2px' }}>
            Everything Conductor can do
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr',
        gap: isMobileLayout ? '6px' : '10px',
        marginBottom: isMobileLayout ? '14px' : '20px'
      }}>
        {displayCapabilities.map((capability) => (
          <div
            key={capability.title}
            style={{
              ...capabilityStyle,
              padding: isMobileLayout ? '9px 11px' : capabilityStyle.padding,
              ...(capability.highlight ? {
                backgroundColor: conductorTheme.surface,
                border: `1px solid ${conductorTheme.border}`,
                boxShadow: '0 1px 2px rgba(10, 13, 18, 0.05)'
              } : {})
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: isMobileLayout ? '13px' : '14px', fontWeight: 600, color: conductorTheme.textStrong }}>
                {capability.title}
              </span>
              {capability.highlight && <Badge color="brand" size="sm">New</Badge>}
            </div>
            <div style={{ fontSize: isMobileLayout ? '12px' : '13px', lineHeight: 1.45, color: conductorTheme.textSoft }}>
              {capability.description}
            </div>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: isMobileLayout ? '16px' : '22px' }}>
        <div style={{ ...eyebrowStyle, marginBottom: isMobileLayout ? '8px' : '10px' }}>
          Try one of these
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobileLayout ? '1fr' : '1fr 1fr', gap: isMobileLayout ? '6px' : '10px' }}>
          {promptsToShow.map((prompt) => (
            <Button
              key={prompt}
              variant="secondary"
              icon={Stars02}
              onClick={() => onPromptSelect(prompt)}
            >
              {prompt}
            </Button>
          ))}
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        marginTop: isMobileLayout ? 'auto' : '4px',
        flexDirection: isMobileLayout ? 'column' : 'row'
      }}>
        {isMobileLayout ? null : (
          <Button variant="tertiary" size="md" icon={ArrowLeft} onClick={() => setStep(1)}>
            Back
          </Button>
        )}
        {isMobileLayout ? <div>{stepDots}</div> : stepDots}
        <Button variant="primary" size="md" style={primaryButtonStyle} onClick={onStart}>
          Start exploring
        </Button>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    <div style={overlayStyle}>
      <div style={takeoverStageStyle}>
        <div style={takeoverFloorStyle} />
        <div style={takeoverColumnStyle('6%', '16%', '34%', 0.9)} />
        <div style={takeoverColumnStyle('26%', '22%', '42%', 0.8)} />
        <div style={takeoverColumnStyle('54%', '18%', '36%', 0.72)} />
        <div style={takeoverColumnStyle('74%', '20%', '40%', 0.86)} />
        {sparklesToRender.map((sparkle, index) => (
          <img
            key={`${sparkle.left}-${sparkle.delay}`}
            src="/conductor-shimmer.svg"
            alt=""
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: sparkle.left,
              bottom: '-48px',
              width: `${sparkle.size}px`,
              height: `${sparkle.size}px`,
              opacity: 0,
              transformOrigin: 'center center',
              animation: `conductor-sparkle-flyaway ${sparkle.duration}s ease-out infinite`,
              animationDelay: `${sparkle.delay}s`,
              '--sparkle-drift': `${sparkle.drift}px`,
              '--sparkle-depth': `${1 + ((index % 4) * 0.06)}`
            }}
          />
        ))}
        <style>{`
          @keyframes conductor-sparkle-flyaway {
            0% {
              opacity: 0;
              transform: translate3d(0, 0, 0) scale(calc(0.7 * var(--sparkle-depth)));
            }
            12% {
              opacity: 0.95;
            }
            55% {
              opacity: 1;
            }
            100% {
              opacity: 0;
              transform: translate3d(var(--sparkle-drift), -115vh, 0) scale(calc(1.08 * var(--sparkle-depth)));
            }
          }
          @keyframes conductor-step-in {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
      <div style={responsiveShellStyle}>
        {closeButton}
        <div
          ref={stepRef}
          tabIndex={-1}
          role="group"
          aria-label={`Step ${step} of 2`}
          style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, outline: 'none' }}
        >
          {step === 1 ? stepOne : stepTwo}
        </div>
      </div>
    </div>,
    document.body
  );
};

VenueConductorLaunchTour.propTypes = {
  visible: PropTypes.bool.isRequired,
  teamName: PropTypes.string,
  onClose: PropTypes.func,
  onStart: PropTypes.func,
  onPromptSelect: PropTypes.func,
  conductorVoiceEnabled: PropTypes.bool,
  hasAiIntegration: PropTypes.bool
};

VenueConductorLaunchTour.defaultProps = {
  teamName: null,
  onClose: () => {},
  onStart: () => {},
  onPromptSelect: () => {},
  conductorVoiceEnabled: false,
  hasAiIntegration: false
};

export default VenueConductorLaunchTour;
